// Firestore REST reader for cold-boot reads: a plain GET on the preconnected origin
// skips the SDK's cold WebChannel handshake (~700ms). First-paint public reads only;
// everything else stays on the SDK.

const PROJECT_ID = process.env.VUE_APP_FIREBASE_PROJECT_ID
const API_KEY = process.env.VUE_APP_FIREBASE_API_KEY
const BASE = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`

// GET a document by path (e.g. 'hostnames/vangogh'). Returns typed-JSON `fields`,
// null if missing (404); throws otherwise so callers can fall back to the SDK.
// Pass `token` (Firebase ID token) for auth-gated documents; omit for public reads.
export async function restGetDocFields(path, token = null) {
  const url = token ? `${BASE}/${path}` : `${BASE}/${path}?key=${API_KEY}`
  const options = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
  const res = await fetch(url, options)
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Firestore REST ${res.status} for ${path}`)
  return (await res.json()).fields || {}
}

// POST a structured query under a parent document (e.g. 'users/{uid}'), or a root collection
// when parentPath is ''. Returns plain doc objects ({ id, ...fields }) in query order.
export async function restRunQuery(parentPath, structuredQuery) {
  const base = parentPath ? `${BASE}/${parentPath}` : BASE
  const res = await fetch(`${base}:runQuery?key=${API_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ structuredQuery })
  })
  if (!res.ok) throw new Error(`Firestore REST ${res.status} for ${parentPath || '(root)'}:runQuery`)
  return restRowsToPlain(await res.json())
}

// POST a count aggregation for a structured query under a parent document.
// Returns the count as a number; throws on failure (callers fall back to the SDK).
export async function restCountQuery(parentPath, structuredQuery) {
  const base = parentPath ? `${BASE}/${parentPath}` : BASE
  const res = await fetch(`${base}:runAggregationQuery?key=${API_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      structuredAggregationQuery: {
        structuredQuery,
        aggregations: [{ alias: 'count', count: {} }]
      }
    })
  })
  if (!res.ok) throw new Error(`Firestore REST ${res.status} for ${parentPath || '(root)'}:runAggregationQuery`)
  const rows = await res.json()
  const value = rows?.[0]?.result?.aggregateFields?.count?.integerValue
  if (value == null) throw new Error('Firestore REST aggregation returned no count')
  return Number(value)
}

// Typed-JSON value for a JS number (query cursors/filters).
export function restNumberValue(n) {
  return Number.isInteger(n) ? { integerValue: String(n) } : { doubleValue: n }
}

// runQuery response rows -> plain doc objects ({ id, ...fields }) in query order.
export function restRowsToPlain(rows) {
  return rows
    .filter(row => row.document) // rows without `document` are read-time markers
    .map(row => ({
      id: row.document.name.split('/').pop(),
      ...restFieldsToPlain(row.document.fields)
    }))
}

// Consume the index.html inline first-24 works prefetch. Single-use and uid-checked;
// returns plain works or null (caller then runs its own query).
export async function restConsumePrefetchedWorks(uid) {
  const pre = window.__worksFirstBatch
  if (!pre) return null
  window.__worksFirstBatch = null
  try {
    const result = await pre
    if (result && result.uid === uid && Array.isArray(result.rows)) return restRowsToPlain(result.rows)
  } catch { /* fall through */ }
  return null
}

// Read a string field from typed-JSON fields: { uid: { stringValue: 'x' } } -> 'x'
export function restString(fields, name) {
  return fields?.[name]?.stringValue ?? null
}

function mapBundle({ uid, config, home }) {
  return {
    uid: uid || null,
    config: config ? restFieldsToPlain(config) : null,
    home: home ? restFieldsToPlain(home) : null
  }
}

// Edge-cached /__site bundle: { uid, config, home } mapped to plain objects, null on any
// failure (callers fall back to direct reads). Memoized so config and home share one fetch.
let _siteBundlePromise = null
export function restGetSiteBundle() {
  if (_siteBundlePromise) return _siteBundlePromise
  _siteBundlePromise = (async () => {
    try {
      // Reuse the index.html inline fetch; consumed once so refresh-on-save refetches
      const inline = window.__siteBundle
      if (inline) {
        window.__siteBundle = null
        const raw = await inline
        if (raw) return mapBundle(raw)
      }
      const res = await fetch('/__site')
      if (!res.ok) return null
      return mapBundle(await res.json())
    } catch {
      return null
    }
  })()
  return _siteBundlePromise
}

// Rebuild a tenant's edge bundle after an admin save (admin-only). `baseUrl` is the tenant's
// live origin (e.g. https://picavera.com) - the admin app runs on vitrinis.com, so this is a
// cross-origin call the Worker permits via CORS and gates by verifying `token` belongs to that
// tenant. Resets the memoized read. Best-effort: failures fall back to the edge TTL.
// Returns 'ok' on a confirmed rebuild, otherwise the failure description.
export async function refreshSiteBundle(token, baseUrl = '') {
  if (!token) return 'no token'
  _siteBundlePromise = null
  let outcome = 'no attempt'
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      // `_` cache-buster + `cache: 'no-store'`: the refresh must reach the Worker every
      // time. Without a unique URL a browser can serve a previously-cached refresh
      // response from disk and the rebuild silently stops firing. (Worker also returns
      // no-store on this response now; this is belt-and-suspenders for already-poisoned caches.)
      // `keepalive`: the refresh must survive the admin navigating away right after a save.
      const res = await fetch(`${baseUrl}/__site?refresh=1&_=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        keepalive: true
      })
      // Only x-edge-cache: REFRESH proves a rebuild - a skipped refresh returns stale
      // data as a plain 200, so res.ok alone proves nothing. Retry once on anything else.
      if (res.ok && res.headers?.get('x-edge-cache') === 'REFRESH') return 'ok'
      // x-edge-refresh carries the Worker's skip reason.
      outcome = `status ${res.status}, ${res.headers?.get('x-edge-refresh') || 'no skip reason'}`
    } catch (err) {
      outcome = `fetch failed: ${err?.message || err}`
    }
  }
  // Non-fatal: edge keeps serving until TTL. Log so we can spot repeated failures.
  console.warn(`refreshSiteBundle: edge rebuild not confirmed for ${baseUrl} (${outcome})`)
  return outcome
}

// Convert one typed-JSON value to a plain JS value. Timestamps become Date objects
// (the SDK gives Timestamp with .toDate() - consumers must accept both).
function fromRestValue(v) {
  if (v == null) return null
  if ('stringValue' in v) return v.stringValue
  if ('booleanValue' in v) return v.booleanValue
  if ('integerValue' in v) return Number(v.integerValue)
  if ('doubleValue' in v) return v.doubleValue
  if ('timestampValue' in v) return new Date(v.timestampValue)
  if ('mapValue' in v) return restFieldsToPlain(v.mapValue.fields)
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(fromRestValue)
  if ('referenceValue' in v) return v.referenceValue
  return null // nullValue and anything unrecognized
}

// Convert a document's typed-JSON `fields` into a plain object (recursive).
export function restFieldsToPlain(fields) {
  const out = {}
  for (const [key, value] of Object.entries(fields || {})) out[key] = fromRestValue(value)
  return out
}
